import React, { useState } from 'react';
import { ChevronDown } from 'lucide-react';

const advisorQuestions = [
  {
    question: "What is MyInvestFolio for Trusted Professionals?",
    answer: "MyInvestFolio allows a select number of trusted professionals — including wealth and investment advisors, accountants, lawyers, tax consultants, financial planners and estate planners — to introduce clients to a platform that gives those clients a clear view of their own portfolios."
  },
  {
    question: "How MyInvestFolio Works for Trusted Professionals",
    answer: null,
    steps: [
      "Privately introduce your clients to MyInvestFolio, a secure portfolio platform built for discerning investors to gain portfolio clarity, without losing privacy.",
      "Clients Set Up Privately: Each client creates and manages their own portfolio.",
      "Better Conversations: Clients gain clarity, enabling more focused and informed discussions with you.",
      "The Investors (Your clients) stay in control. They own their data. You never see their portfolio unless they choose to share outside MyInvestFolio. They add and manage their own data privately.",
      "Strengthen your professional role. When clients have a clear view of their investments, your conversations become more strategic and productive.",
      "Privacy-first, always. MyInvestFolio is designed so that even our own team cannot view client data. Your recommendation reinforces your commitment to their privacy."
    ]
  },
  {
    question: "Can I view my clients' portfolios in MyInvestFolio?",
    answer: "No. MyInvestFolio is designed for investors to manage their own data privately. You can introduce the platform; your clients decide how and with whom to share their information."
  },
  {
    question: "How do I privately introduce a client to MyInvestFolio?",
    answer: "Simply share the Client's full name and email address with MyInvestFolio via a secure private introduction link. MyInvestFolio will complete the rest of the process."
  },
  {
    question: "Does MyInvestFolio compete with my services?",
    answer: "No. MyInvestFolio is a portfolio management tool. It gives clients clarity, not advice. It complements your role by helping them organize the data you need to provide them with better guidance."
  },
  {
    question: "Is there a way for me to receive client reports?",
    answer: "Your clients can export summaries or share static reports with you directly. MyInvestFolio does not grant trusted professionals live access to client dashboards."
  },
  {
    question: "What is my role as a Trusted Professional on MyInvestFolio?",
    answer: "To guide and support clients, while they maintain full control of their investment data."
  },
  {
    question: "Why would I introduce MyInvestFolio to clients?",
    answer: "MyInvestFolio helps clients better understand their portfolios, leading to more informed and productive discussions. Clients who use MyInvestFolio arrive for meetings with organized data, clear questions, and a deeper understanding of their portfolio. That makes your conversations more strategic and efficient. Private introduction access is available to a select number of trusted professionals."
  },
  {
    question: "What if my client needs help using MyInvestFolio?",
    answer: "We have walkthrough videos for new investors onboarding. Our support team is available to assist them directly, either via our Quick Virtual Setup Session or the Deep Private Virtual Assisted Onboarding. You are never expected to be a technical resource for the platform."
  },
  {
    question: "How does MyInvestFolio ensure client data stays private?",
    answer: "All data is encrypted and accessible only by the client. Even MyInvestFolio's own team cannot view a portfolio without explicit, revocable permission from the client."
  },
  {
    question: "If I recommend MyInvestFolio, am I liable for any data issues?",
    answer: "No. Your introduction is just that - a suggestion. Clients make their own decisions about using the platform. We maintain full responsibility for security and privacy."
  },
  {
    question: "What if a client wants me to help them set up their portfolio?",
    answer: "That's between you and your client. If you choose to assist, you can sit with them while they enter data, but you will not have a separate login access."
  },
  {
    question: "Is there a cost for trusted professionals?",
    answer: "No. Trusted professionals never pay for MyInvestFolio, except in cases where they also use MyInvestFolio for their own personal investment portfolios. Only Investors subscribe to MyInvestFolio."
  },
  {
    question: "What if a client shares their login with me?",
    answer: "We discourage sharing credentials. Our terms of use require each user to maintain their own account. If a client wants you to see their data, they can export reports or share screens."
  },
  {
    question: "Can I see what my clients enter on MyInvestFolio?",
    answer: "No. You do not have visibility into client portfolios."
  },
  {
    question: "Can clients share their portfolio with me?",
    answer: "Only if they choose to do so outside the platform. MyInvestFolio does not provide trusted professionals access to clients' investment data."
  },
  {
    question: "Can I use MyInvestFolio to manage my own investments?",
    answer: "Yes. Many trusted professionals use MyInvestFolio for their personal portfolios. That gives you firsthand experience to speak confidently about the platform to your clients."
  },
  {
    question: "Is MyInvestFolio compliant with financial regulations and cyber security standards?",
    answer: "MyInvestFolio is a portfolio management tool, not an investment advisor. We meet industry security standards and can provide documentation for your compliance files."
  },
  {
    question: "What if my client finds MyInvestFolio difficult to use?",
    answer: "We provide onboarding support in various ways. Clients can also reach our support team directly. We're committed to making the experience smooth."
  },
  {
    question: "How do clients benefit most from MyInvestFolio?",
    answer: "They gain a single, secure view of all private investments: fixed deposits, quoted stocks, treasury bills, bonds, funds, private equity, real estate, etc., along with portfolio asset allocation and performance metrics. It saves them time and gives them confidence."
  }
];

const AdvisorResources = () => {
  const [openIndex, setOpenIndex] = useState(null);

  const toggle = (idx) => setOpenIndex(openIndex === idx ? null : idx);

  return (
    <section id="advisor-resources" className="bg-slate-50 py-12 sm:py-20">
      <div className="container mx-auto px-4 sm:px-6">
        <div className="text-center mb-8 sm:mb-12">
          <h2 className="text-2xl sm:text-3xl lg:text-4xl font-serif font-bold text-slate-900">
            Trusted Professional Resources
          </h2>
          <p className="text-base sm:text-lg text-slate-600 mt-3 max-w-2xl mx-auto">
            Information for wealth advisors, accountants, lawyers, financial planners and other trusted professionals introducing clients to MyInvestFolio.
          </p>
        </div>

        <div className="max-w-3xl mx-auto space-y-3">
          {advisorQuestions.map((item, idx) => (
            <div
              key={`res-${idx}`}
              data-testid={`advisor-faq-${idx}`}
              className="border border-slate-200 rounded-xl overflow-hidden bg-white"
            >
              <button
                onClick={() => toggle(idx)}
                className="w-full flex items-center justify-between px-5 py-4 text-left hover:bg-slate-50 transition"
              >
                <span className="font-semibold text-slate-800 text-sm sm:text-base pr-4">{item.question}</span>
                <ChevronDown
                  className={`w-5 h-5 text-slate-400 shrink-0 transition-transform duration-200 ${openIndex === idx ? 'rotate-180' : ''}`}
                />
              </button>
              {openIndex === idx && (
                <div className="px-5 pb-4 text-sm sm:text-base text-slate-600 leading-relaxed">
                  {item.answer && <p>{item.answer}</p>}
                  {item.steps && (
                    <div className="space-y-3 mt-1">
                      {item.steps.map((step, sIdx) => (
                        <div key={`step-${sIdx}-${step.slice(0, 20)}`} className="flex gap-3">
                          <div className="shrink-0 w-6 h-6 rounded-full bg-blue-100 text-blue-700 flex items-center justify-center text-xs font-bold mt-0.5">
                            {sIdx + 1}
                          </div>
                          <p className="text-slate-600">{step}</p>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default AdvisorResources;
